import {
  AssociationEntity,
  IPayment,
  PAYMENT_STATUS_DISPLAY,
} from '@monetize/types/app';
import { useQueryClient } from '@tanstack/react-query';
import { FC, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  useGetAccountById,
  useGetPaymentById,
} from '../../../api/accountsService';
import { usePrintPaymentToHtml } from '../../../api/paymentsService';
import { accountServiceQueryKeys } from '../../../api/queryKeysService';
import { ExternalAssociationsIcons } from '../../../components/ExternalAssociations/ExternalAssociationsIcons';
import {
  MBox,
  MFlex,
  MPageContainer,
  MPageHeader,
} from '../../../components/Monetize';
import { HtmlIFrameContentRenderer } from '../../../components/Monetize/HtmlIFrameContentRenderer';
import { PaymentDetails } from '../../../components/Payments/PaymentDetails';
import { ROUTES } from '../../../constants';
import { PaymentActions } from './PaymentActions';

export const PaymentPage: FC = () => {
  const params = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const paymentId = params?.paymentId || '';
  const accountId = params?.accountId || '';

  /**
   * Manual cache check to prevent duplicate API calls.
   * While React Query should automatically handle cached data, it doesn't always work
   * reliably in this scenario when navigating from IdPrefixNavigator. We manually check
   * the cache and use the data as initialData while disabling the query to ensure
   * no duplicate requests are made.
   */
  const existingCacheData = paymentId
    ? queryClient.getQueryData<IPayment>(
        accountServiceQueryKeys.payments.paymentById(paymentId),
      )
    : undefined;

  const {
    data: payment,
    isLoading: isPaymentIdLoading,
    isError,
    refetch,
  } = useGetPaymentById(accountId, paymentId, {
    enabled: !!accountId && !!paymentId && !existingCacheData,
    meta: { showErrorToast: true },
  });

  useEffect(() => {
    if (accountId && isError) {
      navigate(ROUTES.getAccountPaymentsRoute(accountId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId, isError]);

  const { data: accountDetails } = useGetAccountById(accountId, {
    enabled: !!accountId,
    refetchOnWindowFocus: false,
  });

  const { data, isLoading, isRefetching, error, isPending } =
    usePrintPaymentToHtml({
      paymentId,
    });

  if (error) {
    // TODO: add error state
    return null;
  }

  return (
    <MPageContainer height="auto" enableAccountSearch>
      <MPageHeader
        hasBackButton
        title="View Payment"
        status={
          payment?.paymentStatus
            ? PAYMENT_STATUS_DISPLAY[payment?.paymentStatus]
            : ''
        }
        parentLink={ROUTES.getAccountPaymentsRoute(accountId)}
        parentLinkTitle={accountId && accountDetails?.accountName}
        copyUrl
        id={payment?.id}
        recordAuditInfo={payment}
        extraBadges={[
          <ExternalAssociationsIcons
            key="ExternalAssociationsIcons"
            entity={AssociationEntity.PAYMENT}
            recordId={payment?.id || paymentId}
          />,
        ]}
      >
        <MFlex
          alignContent="center"
          alignItems="center"
          columnGap={3}
          display={['none', 'none', 'flex']}
        >
          {!!payment?.id && (
            <PaymentActions
              payment={payment}
              accountId={accountId}
              refetch={refetch}
            />
          )}
        </MFlex>
      </MPageHeader>

      <MBox
        width="100%"
        px={[1, 1, 8]}
        display="flex"
        flexDirection="column"
        alignItems="center"
      >
        {!isPaymentIdLoading && accountDetails && payment && (
          <PaymentDetails
            payment={payment}
            accountId={accountId}
            refetch={refetch}
          />
        )}
        <MBox
          width="100%"
          mt={6}
          borderWidth={0.5}
          borderColor="tBlue.hover"
          background="tWhite.titanWhite"
        >
          <HtmlIFrameContentRenderer
            id="payment-content"
            html={data}
            isLoading={isLoading}
            isReFetching={isPending || isRefetching}
          ></HtmlIFrameContentRenderer>
        </MBox>
      </MBox>
    </MPageContainer>
  );
};
